<template>
	<component :is="element">
		<slot name="header" />

		<template v-if="status === 'pending' || status === 'idle'">
			<div v-for="index in 7" :key="index" class="skeleton-gradient h-5 w-full mb-2 rounded-lg" />
			<div class="skeleton-gradient h-9 w-36 mt-4 rounded-xl" />
		</template>

		<template v-else-if="weekMenuList?.menu">
			<template v-for="(day, dayIndex) in weekMenuList.menu.listOfMeals" :key="dayIndex">
				<div v-if="todayMenuIndex(dayIndex) && day.meals.length" class="space-y-2">
					<div v-for="(meal, mealIndex) in day.meals" :key="mealIndex" class="flex gap-2 lg:gap-4 w-full">
						<span v-if="showAmount" class="min-w-[2.5rem] lg:min-w-[3.125rem]">
							{{ meal.amount }}
						</span>
						<span class="flex-auto">
							{{ meal.name }}
							<template v-if="getAllergenIds(meal.allergensCheckboxes).join().length && showAllergens">
								({{ getAllergenIds(meal.allergensCheckboxes).join() }})
							</template>
							<template v-for="diet in meal.dietCheckboxes" :key="diet.id">
								<NuxtIcon
									v-if="diet.value && getDietIconName(diet.id)?.length"
									filled
									:name="getDietIconName(diet.id)"
									class="[&>svg]:inline [&>svg]:-translate-y-[2px] text-icon-neutral"
									:aria-label="diet.label"
								/>
							</template>
						</span>
						<strong class="min-w-[2.5rem] lg:min-w-[3.125rem] text-end">
							{{ formatPrice(meal.price) }}
						</strong>
					</div>
				</div>
				<span v-else-if="todayMenuIndex(dayIndex) && !day.meals.length" class="text-sm italic block">
					Menu pro tento den je prázdné
				</span>
			</template>
			<slot name="footer" />
		</template>

		<div v-else>
			Žádné menu nenalezeno
		</div>
	</component>
</template>

<script setup lang="ts">
	import {startOfWeek, isSameDay, addDays} from 'date-fns';
	import {storeToRefs} from 'pinia';
	import {formatPrice} from '@/utils';
	import {getAllergenIds, getDietIconName} from '@/utils/foodMenu';
	import {useStoryblokHypermarketsStore} from '~/stores/storyblokHypermarkets';
	import {useFoodMenuStore} from '~/stores/foodMenu';
	import {getBranchStoryblokSlug} from '~/utils/branch';

	type Props = {
		healthy?: boolean;
		showAmount?: boolean;
		showAllergens?: boolean;
		sourceBranch?: string;
		element?: string;
	};

	const props = withDefaults(defineProps<Props>(), {element: 'div'});

	const systemDate = useState<string>('shared-system-date', () => new Date().toISOString());

	const hypermarketsStore = useStoryblokHypermarketsStore();
	const {selectedHypermarket} = storeToRefs(hypermarketsStore);

	const foodMenuStore = useFoodMenuStore();
	const {menuList} = storeToRefs(foodMenuStore);
	const sourceBranch = computed(() => props.sourceBranch ? props.sourceBranch : selectedHypermarket.value);
	const folder = computed(() => sourceBranch.value
		? getBranchStoryblokSlug(
			sourceBranch.value,
			`sluzby/restaurace/${props.healthy ? 'zdrave-bistro' : 'klasicke-menu'}`,
		)
		: '',
	);
	const {data: weekMenuList, status} = useLazyAsyncData(
		`foodMenuToday-${folder.value || 'none'}`,
		async () => {
			if (!folder.value) {
				return null;
			}

			await foodMenuStore.fetchMenu(folder.value, startOfWeek(new Date()));
			return menuList.value[folder.value] || null;
		},
		{watch: [folder]},
	);

	const todayMenuIndex = computed(() => (dayIndex: number | string) => {
		if (!weekMenuList.value?.menu?.dataFrom) {
			return false;
		}

		const targetDate = addDays(new Date(weekMenuList.value.menu.dataFrom), Number(dayIndex));
		return isSameDay(targetDate, new Date(systemDate.value));
	});
</script>
